import { Business, TenantBusiness } from "@turizi/shared";
import { formatDay, formatDayShort, formatPrice } from "@/lib/format-utils";
import { ServiceTitleStatic } from "@/components/services/service-title";
import { notify } from "@/lib/notify";
import { TFunction } from "i18next";
import { localTenant } from "@/services/session-service";

export interface PinnedTariffItem {
    id: string;
    service: any;
    pricePeriod: any;
    tierKey: string;
    quantity: number;
    nights?: number;
}

export interface PinnedItemPriceInfo {
    originalUnitAmount: number;
    discountPercentage: number;
    finalUnitAmount: number;
    currency: string;
    start_day?: string | null;
    end_day?: string | null;
}

export function applyDiscountPercentage(amount: number, percentage: number): number {
    if (!percentage || percentage <= 0) return amount;
    return Math.ceil(amount * (1 - percentage / 100));
}

export function getPinnedItemPriceInfo(
    item: PinnedTariffItem,
    client?: TenantBusiness
): PinnedItemPriceInfo {
    const p = item.pricePeriod;
    let amount = 0;
    let currency = '--';

    if (!p) {
        return {
            originalUnitAmount: 0,
            discountPercentage: 0,
            finalUnitAmount: 0,
            currency: '--',
            start_day: null,
            end_day: null
        };
    }

    if (item.tierKey === 'default') {
        amount = p.default_amount;
        currency = p.default_currency;
    } else if (item.tierKey === 'foreigner') {
        amount = p.foreigner_amount ?? p.default_amount;
        currency = p.foreigner_currency ?? p.default_currency;
    } else if (item.tierKey === 'child') {
        amount = p.child_amount ?? p.default_amount;
        currency = p.child_currency ?? p.default_currency;
    } else if (item.tierKey === 'senior') {
        amount = p.senior_amount ?? p.default_amount;
        currency = p.senior_currency ?? p.default_currency;
    } else if (item.tierKey === 'infant') {
        amount = p.infant_amount ?? p.default_amount;
        currency = p.infant_currency ?? p.default_currency;
    }

    const clientDiscountCategory = client?.discount_category;
    let discountPercentage = 0;
    if (clientDiscountCategory && item.service?.discounts) {
        const discountCategory = item.service.discounts.find(
            (discount: any) => discount.category === clientDiscountCategory
        );
        if (discountCategory && discountCategory.discounts?.length) {
            discountPercentage = discountCategory.discounts.find(
                (discount: any) => discount.currency === currency
            )?.percentage || 0;
        }
    }

    const finalUnitAmount = applyDiscountPercentage(amount, discountPercentage);

    return {
        originalUnitAmount: amount,
        discountPercentage,
        finalUnitAmount,
        currency,
        start_day: p.start_day,
        end_day: p.end_day
    };
}

export function calculatePinnedTotals(
    pinnedItems: PinnedTariffItem[],
    selectedClient?: Business | null
): Record<string, number> {
    const acc: Record<string, number> = {};
    pinnedItems.forEach(item => {
        const clientObj = selectedClient?.type === 'business' ? selectedClient.tenant_business : selectedClient;
        const info = getPinnedItemPriceInfo(item, clientObj as TenantBusiness);
        if (info) {
            const isPaxBased = !item.service?.price_by || item.service.price_by === 'pax' || item.service.price_by === 'night_and_pax';
            const isNightBased = item.service?.price_by === 'night' || item.service.price_by === 'night_and_pax';
            const minPax = isPaxBased ? (item.service?.min_pax_per_booking || 0) : 0;
            const qty = isPaxBased ? Math.max(item.quantity, minPax > 1 ? minPax : 1) : 1;
            const nights = isNightBased ? Math.max(item.nights || 1, 1) : 1;
            const itemTotal = info.finalUnitAmount * qty * nights;
            acc[info.currency] = (acc[info.currency] || 0) + itemTotal;

            acc[info.currency] = Math.ceil(acc[info.currency]);
        }
    });
    return acc;
}

export function generateTariffSummaryText(
    pinnedItems: PinnedTariffItem[],
    selectedClient: Business | null,
    totals: Record<string, number>,
    t: TFunction
): string {
    const tenant = localTenant();
    const tenantName = tenant?.settings?.store?.display_name || tenant?.name;
    const currentDate = formatDay(new Date());

    let text = `${t('budget').toUpperCase()}: `;
    text += `*${currentDate}*\n`;
    if (tenantName) {
        text += `${t('agency')}: *${tenantName}*\n`;
    }
    if (selectedClient) {
        text += `${t('client')}: *${selectedClient.name}*\n`;
    }
    text += '\n';

    pinnedItems.forEach(item => {
        const priceInfo = getPinnedItemPriceInfo(
            item,
            selectedClient?.type === 'business' ? selectedClient.tenant_business : undefined
        );
        const isPaxBased = !item.service.price_by || item.service.price_by === 'pax' || item.service.price_by === 'night_and_pax';
        const isNightBased = item.service.price_by === 'night' || item.service.price_by === 'night_and_pax';
        const minPax = item.service.min_pax_per_booking || 0;
        const maxPax = item.service.max_pax_per_booking || 0;
        const qty = isPaxBased ? Math.max(item.quantity, minPax > 1 ? minPax : 1) : 1;
        const nights = isNightBased ? Math.max(item.nights || 1, 1) : 1;
        const name = ServiceTitleStatic(item.service).toUpperCase();

        const paxLimits: string[] = [];
        if (minPax > 1) {
            paxLimits.push(`${t('min_pax')}: ${minPax}`);
        }
        if (maxPax > 0) {
            paxLimits.push(`${t('max_pax')}: ${maxPax}`);
        }
        const qtyText = qty > 1 ? `[x${qty}]` : '';

        text += `• *${name}* ${qtyText} \n`;
        if (paxLimits.length > 0) {
            text += ` - ${paxLimits.join(' • ')}\n`;
        }

        if (priceInfo) {
            const priceByText = item.service.price_by ? ` ${t('price_by_' + item.service.price_by)}` : '';
            const datesText = (priceInfo.start_day || priceInfo.end_day)
                ? ` (${formatDayShort(priceInfo.start_day)}${priceInfo.end_day ? ` - ${formatDayShort(priceInfo.end_day)}` : ''})`
                : '';
            const itemBusiness = item.pricePeriod?.business || (item.pricePeriod?.business_id ? item.service?.providers?.find((pr: any) => pr.id === item.pricePeriod.business_id) : null);
            const providerText = item.pricePeriod?.business_id
                ? ` [${itemBusiness?.name || "business_name_missing"}]`
                : ``;
            const tierText = item.tierKey === 'senior' && item.service?.show_retired ? t('retired') : t(item.tierKey);
            text += ` - ${t('standard_retail_price')} ${tierText}: *$ ${formatPrice(priceInfo.originalUnitAmount)} ${priceInfo.currency}*${priceByText}${datesText}${providerText}\n`;
            if (priceInfo.discountPercentage > 0) {
                text += ` - ${t('discount')}: ${priceInfo.discountPercentage}%\n`;
            }
            if (isNightBased) {
                text += ` - ${t('nights')}: *${nights}*\n`;
            }
            text += ` - ${t('net_price')}: *$ ${formatPrice(priceInfo.finalUnitAmount * qty * nights)} ${priceInfo.currency}*\n`;
        }
        text += '\n';
    });

    text += '\n\n';
    Object.entries(totals).forEach(([currency, amount]) => {
        text += `${t('total').toUpperCase()} *$ ${formatPrice(amount)} ${currency}*\n`;
    });

    return text;
}

export function copySummaryToClipboard(
    pinnedItems: PinnedTariffItem[],
    selectedClient: Business | null,
    totals: Record<string, number>,
    t: TFunction
): Promise<void> {
    const text = generateTariffSummaryText(pinnedItems, selectedClient, totals, t);
    return navigator.clipboard.writeText(text).then(() => {
        notify.success('success', 'summary_copied');
    });
}
